<template>
    <div class="container mx-auto">
        <steps-viewer
            :steps="$data.$steps"
            :step="currentStep"
            @click="clickStep"
        />
        <div
            v-if="currentStep === 0"
            class="w-full md:w-10/12 lg:w-8/12 mx-auto"
        >
            <div class="flex flex-col justify-center">
                <div class="flex items-center justify-center mb-4">
                    <span class="display-4 mr-2">🗃</span>
                    <p
                        class="text-theme-primary text-xl font-semibold"
                        data-testid="default-typography"
                    >
                        Sube el archivo con el inventario
                    </p>
                </div>

                <div
                    class="flex items-center justify-between p-4 border rounded-xl mb-4"
                >
                    <div class="flex-1 text-sm text-theme-base pr-4">
                        <p data-testid="default-typography">
                            Carga de inventario para la
                            {{
                                selectedMerchant.merchantType
                                    | merchantInfo("type")
                            }}
                            <strong>"{{ selectedMerchant.name | trim }}"</strong
                            >. Este proceso reemplazará o ingresará las unidades
                            del inventario. Máximo <strong>1000 filas</strong>.
                        </p>
                    </div>
                    <a
                        href="/template_massive_load_inventories.xlsx"
                        target="_blank"
                        rel="noopener"
                        class="flex-shrink-0 flex items-center gap-2 px-4 py-2 border-2 border-theme-success text-theme-success font-semibold rounded-lg hover:bg-theme-hover transition-colors text-sm"
                    >
                        ⬇ Descargar plantilla
                    </a>
                </div>

                <inventory-file-loader
                    @loaded="loadInventories"
                    @loading="isLoading = true"
                    @error="isLoading = false"
                />
            </div>
        </div>
        <div v-else-if="currentStep === 1" class="flex flex-col">
            <div class="flex flex-col items-center">
                <p data-testid="default-typography" class="my-4 font-bold">
                    Tienes errores en el archivo. Puedes corregir el archivo y
                    volver al paso anterior, o descartar las filas con error y
                    continuar con las filas válidas.
                </p>
                <div class="flex gap-3">
                    <app-button relief @click="resetItem">
                        Volver al paso anterior
                    </app-button>
                    <app-button warning @click="discardErrorRowsAndContinue">
                        Descartar filas con error y continuar
                    </app-button>
                </div>
            </div>
            <error-viewer
                v-for="(errorItem, index) in errors"
                :key="index"
                :error="errorItem"
                :active="activeError"
                :base="$data.$errorsBase"
                @changeActive="changeActiveError($event)"
            />
        </div>
        <div v-else-if="currentStep === 2">
            <div class="my-3">
                <span>
                    Resumen: inventarios a ingresar
                    {{ inventoriesToInsert.length }} inventarios a
                    {{ overwriteMessage }}
                    {{ inventoriesAlreadyCreated.length }}
                </span>
            </div>
            <div v-if="inventoriesToInsert.length">
                <div class="mb-4">
                    <span class="text-lg">
                        Inventarios a insertar
                        <span class="text-theme-primary">
                            {{ inventoriesToInsert.length }}
                        </span>
                    </span>
                </div>

                <inventory-manage
                    v-for="(item, index) in inventoriesToInsert"
                    :key="'A' + index"
                    :inventory="item"
                    @remove="removeInventoryToInsert"
                    @update="updateInventoryToInsert"
                />
            </div>
            <div v-if="inventoriesAlreadyCreated.length">
                <div class="my-4">
                    <span class="text-lg">
                        Inventarios a {{ overwriteMessage }}
                        <span class="text-theme-primary">
                            {{ inventoriesAlreadyCreated.length }}
                        </span>
                    </span>
                </div>

                <inventory-manage
                    v-for="(item, index) in inventoriesAlreadyCreated"
                    :key="'B' + index"
                    :inventory="item"
                    @remove="removeInventoryToUpdate"
                    @update="updateInventoryToUpdate"
                />
            </div>
            <div class="my-4">
                <app-button
                    class="mt-3 mb-5 w-3/4 mx-auto"
                    border
                    gradient
                    @click="saveMassiveInventoriesComponent"
                >
                    <span class="text-lg"> Guardar cambios ✔ </span>
                </app-button>
            </div>
        </div>
        <div v-else-if="[3, 4].includes(currentStep)">
            <div v-if="isLoading">Procesando ...</div>
            <div v-else class="flex flex-wrap">
                <div class="w-full mb-4">
                    <span class="text-theme-primary text-xl">Resultados</span>
                </div>
                <div class="w-1/2 mt-2">
                    <strong class="h5">
                        Inventarios insertados :
                        <span class="text-theme-primary">
                            {{ results.inventoriesInserted.length }}
                        </span>
                    </strong>
                </div>
                <div class="w-1/2 mt-2">
                    <strong class="h5">
                        Inventarios actualizados :
                        <span class="text-theme-primary">
                            {{ results.inventoriesUpdated.length }}
                        </span>
                    </strong>
                </div>
                <div class="w-1/2 mt-2">
                    <strong class="h5">
                        Inventarios incrementados :
                        <span class="text-theme-primary">
                            {{ results.inventoriesIncremented.length }}
                        </span>
                    </strong>
                </div>

                <div class="w-full flex justify-center mt-4">
                    <app-button @click="resetItem">
                        Iniciar nueva carga
                    </app-button>
                    <app-button success @click="downloadJsonComponent">
                        Descargar Resultados JSON ⬇
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import { downloadJson } from "@/common/lib/json";

import InventoryFileLoader from "@/components/inventories/InventoryFileLoader.vue";
import InventoryManage from "@/components/inventories/InventoryManage.vue";
import ErrorViewer from "@/components/utils/ErrorViewer.vue";
import StepsViewer from "@/components/utils/StepsViewer.vue";

export default {
    name: "InventoriesMassiveLoad",
    components: {
        InventoryFileLoader,
        StepsViewer,
        ErrorViewer,
        InventoryManage
    },
    data: () => ({
        overwrite: false,
        isLoading: false,
        lastRows: [],
        step: 0,
        $steps: [
            { name: "Sube archivo", enabledClicked: true },
            { name: "Valida errores en archivo" },
            { name: "Verifica el inventario" },
            { name: "Guarda los cambios" }
        ],
        $errorsBase: [
            { name: 'Stock de inventario "stock"', key: "stock" },
            { name: 'Costo "cost"', key: "cost" },
            { name: 'Código de barras "barcode"', key: "barcode" }
        ],
        currentStep: 0,
        activeError: 0,
        inventoriesToInsert: [],
        inventoriesAlreadyCreated: [],
        errors: [],
        results: {
            inventoriesInserted: [],
            inventoriesUpdated: []
        }
    }),
    computed: {
        ...mapState("commons", ["selectedMerchant"]),
        ...mapGetters("control", ["backgroundColor"]),
        overwriteMessage() {
            return this.overwrite ? "actualizar" : "incrementar";
        }
    },
    watch: {
        selectedMerchant() {
            this.resetItem();
        }
    },
    async mounted() {
        this.overwrite =
            this.$route.name === "InventoriesMassiveLoadAndUpdates";
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (
            !(await verifyPagePermission(
                "inventory:LoadMassive",
                "/app/inventories",
                true
            ))
        )
            return;
    },
    beforeDestroy() {},
    methods: {
        ...mapActions("inventories", [
            "verifyMassiveInventories",
            "saveMassiveInventories"
        ]),
        async loadInventories(rows) {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: `Vas a cargar ${rows.length} ${
                    rows.length < 2 ? "fila" : "filas"
                } ?`,
                icon: "info",
                showCancelButton: true,
                focusConfirm: false,
                confirmButtonText: "Si",
                cancelButtonText: "No",
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#d33ddd",
                allowOutsideClick: false
            });
            if (!dialogResult.isConfirmed) {
                window.location.href = this.$route.path;
                return;
            }
            this.lastRows = rows;
            this.verifyMassiveInventoriesComponent(rows);
        },
        async verifyMassiveInventoriesComponent(rows) {
            this.errors = [];
            this.inventoriesAlreadyCreated = [];
            this.inventoriesToInsert = [];
            this.isLoading = true;
            this.$loading.show();
            try {
                const result = await this.verifyMassiveInventories({
                    rows,
                    merchantId: this.selectedMerchant.merchantId,
                    merchantType: this.selectedMerchant.merchantType
                });
                if (!result.pass) {
                    this.errors = result.errors;
                    this.activeError = result.errors[0].index;
                    this.currentStep = 1;
                    return;
                }
                this.currentStep = 2;
                this.inventoriesAlreadyCreated =
                    result.inventoriesAlreadyCreated;
                this.inventoriesToInsert = result.inventoriesToInsert;
                if (result.inventoriesAlreadyCreated.length > 0) {
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: `Hemos encontrado referencias anteriormente registradas,
                        recuerda que vas a ${this.overwriteMessage} las unidades en el inventario.`,
                        icon: "info",
                        showCloseButton: true
                    });
                }
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/cash-outputs");
                }
            }
        },
        async saveMassiveInventoriesComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: `Vas a insertar ${this.inventoriesToInsert.length} y a ${this.overwriteMessage} ${this.inventoriesAlreadyCreated.length} unidades en el inventario ?`,
                icon: "info",
                showCancelButton: true,
                focusConfirm: false,
                confirmButtonText: "Si",
                cancelButtonText: "No",
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#d33ddd",
                allowOutsideClick: false
            });
            if (!dialogResult.isConfirmed) {
                return;
            }

            this.isLoading = true;
            this.$loading.show();
            this.currentStep = 3;
            try {
                const inventories = [
                    ...this.inventoriesToInsert,
                    ...this.inventoriesAlreadyCreated
                ].map((item, index) => ({
                    index: index + 1,
                    item: {
                        referenceId: Number(item.id),
                        barcode: item.barcode,
                        stock: Number(item.stock),
                        cost: Number(item.cost),
                        price: Number(item.price),
                        priceOffer: Number(item.priceOffer)
                    }
                }));
                this.results = await this.saveMassiveInventories({
                    inventories,
                    overwrite: this.overwrite,
                    merchantId: this.selectedMerchant.merchantId,
                    merchantType: this.selectedMerchant.merchantType
                });
                this.$analytics.trackEvent("inventory_bulk_uploaded", {
                    items_count: inventories.length,
                    overwrite: this.overwrite
                });
                this.currentStep = 4;
                this.$sound.play();
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                this.currentStep = 3;
            }
        },
        clickStep(step) {
            this.currentStep = step;
        },
        changeActiveError(ref) {
            this.activeError = ref;
        },
        async discardErrorRowsAndContinue() {
            const errorIndexes = new Set(this.errors.map((e) => e.index));
            const filteredRows = this.lastRows.filter(
                (r) => !errorIndexes.has(r.index)
            );
            if (!filteredRows.length) {
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "No quedan filas válidas para procesar",
                    icon: "info"
                });
                this.resetItem();
                return;
            }
            this.verifyMassiveInventoriesComponent(filteredRows);
        },
        resetItem() {
            this.errors = [];
            this.inventoriesAlreadyCreated = [];
            this.inventoriesToInsert = [];
            this.lastRows = [];
            this.currentStep = 0;
        },
        removeInventoryToInsert({ sku }) {
            const index = this.inventoriesToInsert.findIndex(
                (item) => item.sku === sku
            );
            let inventoriesToInsert = [...this.inventoriesToInsert];
            inventoriesToInsert.splice(index, 1);
            this.inventoriesToInsert = [...inventoriesToInsert];
        },
        updateInventoryToInsert(payload) {
            const index = this.inventoriesToInsert.findIndex(
                (item) => item.sku === payload.sku
            );
            let inventoriesToInsert = [...this.inventoriesToInsert];
            inventoriesToInsert[index] = {
                ...payload
            };
            this.inventoriesToInsert = [...inventoriesToInsert];
        },
        removeInventoryToUpdate({ sku }) {
            const index = this.inventoriesAlreadyCreated.findIndex(
                (item) => item.sku === sku
            );
            let inventoriesAlreadyCreated = [...this.inventoriesAlreadyCreated];
            inventoriesAlreadyCreated.splice(index, 1);
            this.inventoriesAlreadyCreated = [...inventoriesAlreadyCreated];
        },
        updateInventoryToUpdate(payload) {
            const index = this.inventoriesAlreadyCreated.findIndex(
                (item) => item.sku === payload.sku
            );
            let inventoriesAlreadyCreated = [...this.inventoriesAlreadyCreated];
            inventoriesAlreadyCreated[index] = {
                ...payload
            };
            this.inventoriesAlreadyCreated = [...inventoriesAlreadyCreated];
        },
        downloadJsonComponent() {
            downloadJson({
                payload: this.results,
                filename: "massiveLoadInventoriesResult"
            });
        }
    }
};
</script>
